import { create } from 'zustand';
import { supabase } from './supabase';
import { toUTC } from './utils/timezone';
import type { TaskOwner } from './types';
import { useAuth } from './auth';

interface ScrumUpdate {
  id: string;
  owner_id: string;
  yesterday: string;
  today: string;
  blockers: string;
  date: string;
  created_at: string;
  owner?: TaskOwner;
}

interface ScrumStore {
  updates: ScrumUpdate[];
  loading: boolean;
  error: string | null;
  fetchUpdates: (date?: string) => Promise<void>;
  createUpdate: (update: Omit<ScrumUpdate, 'id' | 'created_at' | 'owner'>) => Promise<void>;
  updateScrum: (id: string, update: Partial<ScrumUpdate>) => Promise<void>;
  deleteScrum: (id: string) => Promise<void>;
}

export const useScrumStore = create<ScrumStore>((set, get) => ({
  updates: [],
  loading: false,
  error: null,

  fetchUpdates: async (date) => {
    set({ loading: true, error: null });
    try {
      let query = supabase
        .from('scrum_updates')
        .select(`
          *,
          owner:owner_id (
            id,
            name,
            email,
            organization
          )
        `)
        .order('created_at', { ascending: false });

      if (date) {
        query = query.eq('date', toUTC(date));
      }

      const { data, error } = await query;
      if (error) throw error;

      set({ updates: data || [] });
    } catch (error) {
      console.error('Error fetching scrum updates:', error);
      set({ error: error.message });
    } finally {
      set({ loading: false });
    }
  },

  createUpdate: async (update) => {
    set({ loading: true, error: null });
    try {
      const { data, error } = await supabase
        .from('scrum_updates')
        .insert([update])
        .select()
        .single();

      if (error) throw error;
      
      await get().fetchUpdates(update.date);
    } catch (error) {
      console.error('Error creating scrum update:', error);
      set({ error: error.message });
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  updateScrum: async (id, update) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase
        .from('scrum_updates')
        .update(update)
        .eq('id', id);

      if (error) throw error;
      
      if (update.date) {
        await get().fetchUpdates(update.date);
      }
    } catch (error) {
      console.error('Error updating scrum:', error);
      set({ error: error.message });
      throw error;
    } finally {
      set({ loading: false });
    }
  },

  deleteScrum: async (id) => {
    set({ loading: true, error: null });
    try {
      const { error } = await supabase
        .from('scrum_updates')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      // Remove from local state
      set(state => ({
        updates: state.updates.filter(update => update.id !== id)
      }));
    } catch (error) {
      console.error('Error deleting scrum:', error);
      set({ error: error.message });
      throw error;
    } finally {
      set({ loading: false });
    }
  }
}));